/**
 * The admin report's table — quick-260909-k4v.
 *
 * Split out of AdminApp.tsx so it can be rendered against a captured API response without
 * a Cognito session (see the harness pattern in the project notes), which is the only way
 * to eyeball the table's layout: the real page is behind Google sign-in.
 *
 * Presentational only — it takes rows and renders them. No data fetching, no auth.
 */
import type { AdminUserStats } from './adminStatsApi';

/** Render a YYYY-MM-DD or ISO timestamp as a short, unambiguous date. */
export function shortDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? value + 'T00:00:00Z' : value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Human label for a sign-up platform; anything unrecognised is Unknown. */
export function platformLabel(p: string | undefined): string {
  switch (p) {
    case 'ios':
      return 'iOS';
    case 'android':
      return 'Android';
    case 'web':
      return 'Web';
    default:
      return 'Unknown';
  }
}

/** A count cell. Zero is muted so a scan of the table picks out actual activity. */
function Count({ value, tone }: { value: number; tone: 'income' | 'expense' }) {
  if (value === 0) return <span className="text-slate-300">0</span>;
  return (
    <span
      className={
        tone === 'income' ? 'font-medium text-emerald-600' : 'font-medium text-rose-600'
      }
    >
      {value}
    </span>
  );
}

const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500';
const TD = 'px-3 py-2.5 text-sm text-slate-700';

export function StatsTable({ users }: { users: AdminUserStats[] }) {
  if (users.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-sm text-slate-500">
        No registered users to report — every account in the pool is on this environment's
        exclusion list.
      </p>
    );
  }

  return (
    /* Wide table: scrolls inside its own container so the page body never scrolls
       horizontally on a narrow screen. */
    <div className="overflow-x-auto">
      <table className="w-full min-w-[800px] border-collapse">
        <thead>
          {/* Two header rows so the four count columns are grouped under the window they
              belong to — otherwise "In / Out / In / Out" is ambiguous at a glance. */}
          <tr className="border-b border-slate-200">
            <th className={TH} rowSpan={2}>
              User
            </th>
            <th className={TH} rowSpan={2}>
              Signed up
            </th>
            <th className={TH} rowSpan={2}>
              Platform
            </th>
            <th className={`${TH} border-l border-slate-200 text-center`} colSpan={2}>
              Last 30 days
            </th>
            <th className={`${TH} border-l border-slate-200 text-center`} colSpan={2}>
              Next 30 days
            </th>
          </tr>
          <tr className="border-b border-slate-200">
            <th className={`${TH} border-l border-slate-200 text-center`}>Money in</th>
            <th className={`${TH} text-center`}>Money out</th>
            <th className={`${TH} border-l border-slate-200 text-center`}>Money in</th>
            <th className={`${TH} text-center`}>Money out</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.sub} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <td className={TD}>
                <span className="font-medium text-slate-900">{user.email ?? '(no email)'}</span>
                {/* EXTERNAL_PROVIDER is the only status worth calling out — it is how a
                    Google sign-up looks, versus CONFIRMED for email/password. */}
                {user.status === 'EXTERNAL_PROVIDER' && (
                  <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                    Google
                  </span>
                )}
              </td>
              <td className={`${TD} whitespace-nowrap text-slate-500`}>
                {shortDate(user.createdAt)}
              </td>
              <td className={`${TD} whitespace-nowrap`}>
                {user.signupPlatform ? (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                    {platformLabel(user.signupPlatform)}
                  </span>
                ) : (
                  <span className="text-slate-300">{platformLabel(undefined)}</span>
                )}
              </td>
              <td className={`${TD} border-l border-slate-200 text-center`}>
                <Count value={user.counts.last30.income} tone="income" />
              </td>
              <td className={`${TD} text-center`}>
                <Count value={user.counts.last30.expense} tone="expense" />
              </td>
              <td className={`${TD} border-l border-slate-200 text-center`}>
                <Count value={user.counts.next30.income} tone="income" />
              </td>
              <td className={`${TD} text-center`}>
                <Count value={user.counts.next30.expense} tone="expense" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

